import { useState } from 'react';
import { PWA_TEXT } from '@/i18n/pwa';
import {
  currentInstallGuideEnv,
  dismissInstallGuide,
  installGuideDismissed,
  needsIosInstallGuide,
} from './installGuide';
import { applyUpdate } from './registerSW';
import { usePwaState } from './store';
import './pwa.css';

/** task.md §4.14 "clear offline banner": fixed strip at the top while navigator.onLine is false. */
export function OfflineBanner({ online }: { online: boolean }) {
  if (online) return null;
  return (
    <div className="pwa-banner pwa-banner-offline" id="offlineBanner" role="status" aria-live="polite">
      {PWA_TEXT.offline.banner}
    </div>
  );
}

/** A new worker took over: offer a reload so the page runs the new code (and its precached shell). */
export function UpdatePrompt({ ready, onReload }: { ready: boolean; onReload: () => void }) {
  if (!ready) return null;
  return (
    <div className="pwa-banner pwa-banner-update" id="updatePrompt" role="status">
      <span>{PWA_TEXT.update.ready}</span>
      <button type="button" className="btn small" onClick={onReload}>
        {PWA_TEXT.update.reload}
      </button>
    </div>
  );
}

export interface InstallGuideProps {
  show: boolean;
  onDismiss: () => void;
}

/** iOS Safari outside the installed app: how to add the ERP to the Home Screen. */
export function InstallGuide({ show, onDismiss }: InstallGuideProps) {
  if (!show) return null;
  return (
    <div className="pwa-banner pwa-banner-install" id="installGuide" role="note">
      <span>{PWA_TEXT.install.ios}</span>
      <button
        type="button"
        className="btn small ghost"
        onClick={onDismiss}
        aria-label={PWA_TEXT.install.dismiss}
      >
        {PWA_TEXT.install.dismiss}
      </button>
    </div>
  );
}

/** Mounted once above the routes (App.tsx) so the banners show on the login screen too. */
export function PwaBanners() {
  const { online, updateReady } = usePwaState();
  const [guide, setGuide] = useState(
    () => needsIosInstallGuide(currentInstallGuideEnv()) && !installGuideDismissed(),
  );
  const dismiss = () => {
    dismissInstallGuide();
    setGuide(false);
  };
  return (
    <>
      <OfflineBanner online={online} />
      <UpdatePrompt ready={updateReady} onReload={() => void applyUpdate()} />
      <InstallGuide show={guide} onDismiss={dismiss} />
    </>
  );
}
